import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useManagedWorkplaces } from "@src/appV2/PlacementCandidates/hooks/useManagedWorkplaces";
import { useMemo } from "react";

import {
  type PlacementApplicationWithDetails,
  transformResponseIntoPlacementApplication,
  useFetchPaginatedPlacementApplications,
} from "../api/useFetchPaginatedPlacementApplications";
import { type PlacementApplicationStatus } from "../types/fetchPlacementApplications.schema";

interface UsePlacementApplicationStatusOptions {
  placementApplication?: PlacementApplicationWithDetails;
  channelUrl?: string;
}

interface UsePlacementApplicationStatusResult {
  status: PlacementApplicationStatus | undefined;
  isLoading: boolean;
}

/**
 * Hook to get placement application status.
 * Can accept either a placementApplication directly, or a channelUrl to fetch it.
 */
export function usePlacementApplicationStatus(
  options: UsePlacementApplicationStatusOptions
): UsePlacementApplicationStatusResult {
  const { placementApplication: placementApplicationProperty, channelUrl } = options;
  const { profile } = useSession();
  const { workplaces } = useManagedWorkplaces();

  const [, workerId, placementId] = (channelUrl ?? "").split("_");

  const shouldFetchPlacementApplication =
    !placementApplicationProperty &&
    isDefined(channelUrl) &&
    isDefined(profile?.userId) &&
    isDefined(placementId) &&
    isDefined(workerId) &&
    workplaces.length > 0;

  const workplaceFilter = useMemo(
    () =>
      shouldFetchPlacementApplication
        ? workplaces.map((workplace) => workplace.id).join(",")
        : undefined,
    [shouldFetchPlacementApplication, workplaces]
  );

  const filter = useMemo(() => {
    if (!shouldFetchPlacementApplication || !isDefined(placementId) || !isDefined(workerId)) {
      return undefined;
    }

    const result: Record<string, string> = {
      "placement.id": placementId,
      "worker.id": workerId,
    };

    if (isDefined(workplaceFilter) && workplaceFilter.length > 0) {
      result["workplace.id"] = workplaceFilter;
    }

    return result;
  }, [placementId, workerId, workplaceFilter, shouldFetchPlacementApplication]);

  const { data: placementApplicationsData, isLoading } = useFetchPaginatedPlacementApplications(
    {
      workplaceId: profile?.userId ?? "",
      queryParams: {
        filter: filter ?? {},
        page: {
          size: 1,
        },
      },
    },
    {
      enabled: shouldFetchPlacementApplication,
      staleTime: 5 * 60 * 1000,
    }
  );

  const placementApplication = useMemo(() => {
    if (placementApplicationProperty) {
      return placementApplicationProperty;
    }

    if (!placementApplicationsData) {
      return undefined;
    }

    const pages = placementApplicationsData.pages.flatMap((placementApplicationsPage) =>
      placementApplicationsPage.data.map((placementApplication) =>
        transformResponseIntoPlacementApplication(
          placementApplication,
          placementApplicationsPage.included
        )
      )
    );

    return pages.at(0);
  }, [placementApplicationProperty, placementApplicationsData]);

  return {
    status: placementApplication?.status,
    isLoading: shouldFetchPlacementApplication && isLoading,
  };
}
